import JumboContentLayout from "@jumbo/components/JumboContentLayout";
import { useAPI } from "app/hooks/useApi";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";

import * as yup from "yup";
import HeaderPages from "../components/HeaderPages";
import PagesFiltersToolsBar from "../components/PagesFiltersToolsBar";
import PaginationComponent from "../components/Pagination";
import EmptyListWarning from "../components/EmptyListWarning/EmptyListWarning";
import SelectableProcessList from "../components/SelectableProcessList/SelectableProcessList";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardMedia,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from '@mui/icons-material/Delete';
import ClearIcon from '@mui/icons-material/Clear';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const contatoTiposEnum = [
  "FINANCEIRO",
  "RESIDENCIAL",
  "COMERCIAL",
  "JURIDICO",
  "OUTROS"
];

const contatoSchema = yup.object({
  tipo_contato: yup.string().oneOf(contatoTiposEnum, "Tipo inválido").required("Tipo obrigatório"),
  nome: yup.string().required("Nome obrigatório"),
  telefone: yup.string().required("Telefone obrigatório"),
});

const createSchemaValidation = yup.object({
  nome_identificacao: yup.string().required("Campo obrigatório"),
  tipo_envolvido: yup.string().required("Campo obrigatório"),
  tipo_pessoa: yup.string().optional(),
  cpf_cnpj: yup.string().optional(),
  cadastro_externo: yup.string().optional(),
  inscricao_municipal: yup.string().optional(),
  inscricao_estadual: yup.string().optional(),
  contatos: yup.array().of(contatoSchema).min(1, "Adicione ao menos um contato"),
  telefone: yup.string().nullable().optional(), // legado
  email: yup.string().email("Email inválido").nullable().optional(), // legado
});

const updateSchemaValidation = createSchemaValidation;

const EnvolvidosForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { makeRequest } = useAPI();
  const { authUser } = useJumboAuth();

  const initialValues = {
    nome_identificacao: "",
    tipo_envolvido: "CLIENTE",
    tipo_pessoa: "",
    cpf_cnpj: "",
    cadastro_externo: "",
    inscricao_municipal: "",
    inscricao_estadual: "",
    contatos: [{ tipo_contato: "FINANCEIRO", nome: "", telefone: "" }],
    telefone: "",
    email: "",
  };
  const [envolvido, setEnvolvido] = useState(initialValues);
  const [processos, setProcessos] = useState([]);
  const [selectedProcessos, setSelectedProcessos] = useState([]);
  const [totalProcessos, setTotalProcessos] = useState(0);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [sortBy, setSortBy] = useState("id");
  const [sortOrder, setSortOrder] = useState("desc");
  const [carregandoMais, setCarregandoMais] = useState(false);
  const [numeroProcessoBusca, setNumeroProcessoBusca] = useState("");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [envolvidoSubmitResponse, setEnvolvidoSubmitResponse] = useState(null);
  const [processosRelacionados, setProcessosRelacionados] = useState([]);
  const [confirmAddDialogOpen, setConfirmAddDialogOpen] = useState(false);
  const [processoParaAdicionar, setProcessoParaAdicionar] = useState(null);
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [processoParaRemover, setProcessoParaRemover] = useState(null);
  
  // Estados específicos para usuários VIEWER e CLIENTE
  const [observandoEnvolvido, setObservandoEnvolvido] = useState(false);
  const [confirmObservarDialogOpen, setConfirmObservarDialogOpen] = useState(false);
  
  // Verificação de role do usuário
  const isViewer = authUser?.role === "VIEWER";
  const isCliente = authUser?.role === "CLIENTE";
  const isClienteOrViewer = isViewer || isCliente;
  const isAdmin = authUser?.role === "ADMIN";
  const isOperador = authUser?.role === "OPERADOR";
  
  const getEnvolvido = useCallback(async () => {
    if (!id) return;

    const response = await makeRequest(`/envolvidos/${id}`, "GET");

    if (response.status === 200) {

      setEnvolvido(response.data);
    } else {

    }
  }, [id]);

  const getProcessos = useCallback(async () => {

    setCarregandoMais(true);
    let url = `/processos-revistas-rpi?page=${page}&perPage=${perPage}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
    
    // Se estiver editando um envolvido, filtra só pelos processos relacionados a ele
    if (id) {
      url += `&envolvido=${id}`;
    }

    const response = await makeRequest(url, 'GET');
    if (response.status === 200) {
      const novosProcessos = response.data.processos || response.data || [];

      setProcessos(novosProcessos);
      if (response.data.total !== undefined) {
        setTotalProcessos(response.data.total);
      }
    } else {

    }
    setCarregandoMais(false);
  }, [page, perPage, sortBy, sortOrder, id]);
  const handleProcessoToggle = (processoNumero) => {
    const currentIndex = selectedProcessos.indexOf(processoNumero);
    const newSelectedProcessos = [...selectedProcessos];

    if (currentIndex === -1) {
      newSelectedProcessos.push(processoNumero);
    } else {
      newSelectedProcessos.splice(currentIndex, 1);
    }

    setSelectedProcessos(newSelectedProcessos);
  };
  
  const associarProcessos = async (userId) => {
    if (selectedProcessos.length === 0) return;
    
    const userIdNumerico = parseInt(userId, 10);
    
    if (isNaN(userIdNumerico)) {

      return;
    }

    const response = await makeRequest(
      '/users/processos/associar-multiplos',
      'POST',
      {
        userId: userIdNumerico,
        processNumbers: selectedProcessos
      }
    );

    if (response.status === 200) {

      return true;
    } else {

      return false;
    }
  };
  // Novo método para associar envolvido a processo
  const associarEnvolvidoAProcessos = async (envolvidoId) => {
    if (selectedProcessos.length === 0) return;
    for (const processoId of selectedProcessos) {
      await makeRequest(
        `/processos-revistas-rpi/${processoId}/envolvidos`,
        'POST',
        { envolvido_id: envolvidoId }
      );
    }
  };
  const salvarEnvolvido = useCallback(async (values) => {
    let envolvidoData = { ...values };
    delete envolvidoData.createdAt;
    delete envolvidoData.updatedAt;
    delete envolvidoData.deletedAt;
    delete envolvidoData.id;

    // Remove campos legados se contatos estiver preenchido
    if (Array.isArray(envolvidoData.contatos) && envolvidoData.contatos.length > 0) {
      delete envolvidoData.telefone;
      delete envolvidoData.email;
    }

    try {
      const response = await makeRequest(
        `/envolvidos/${id ? id : ""}`,
        id ? "PUT" : "POST",
        envolvidoData
      );

      if ([200, 201].includes(response.status)) {
        return response;
      } else {

        return null;
      }
    } catch (error) {

      return null;
    }
  }, [id]);
  const handleSubmit = useCallback(
    async (values) => {

      if (selectedProcessos.length > 0) {
        setFormValues(values);
        setConfirmDialogOpen(true);
        return;
      }
      const response = await salvarEnvolvido(values);
      if (response) {

        navigate("/envolvidos");
      } else {

      }
    },
    [selectedProcessos, navigate, salvarEnvolvido]
  );
  const handleConfirmProcessAssociation = useCallback(async () => {
    const response = await salvarEnvolvido(formValues);
    if (response) {
      const envolvidoId = id ? id : (response.data && response.data.id);
      if (envolvidoId) {
        await associarEnvolvidoAProcessos(envolvidoId);
      }
    }
    setConfirmDialogOpen(false);
    navigate("/envolvidos");
  }, [formValues, salvarEnvolvido, id, selectedProcessos, navigate]);
  const handleCancelProcessAssociation = () => {
    setConfirmDialogOpen(false);
    navigate("/envolvidos");
  };  const getProcessosRelacionados = useCallback(async () => {
    if (!id) return;

    const response = await makeRequest(`/envolvidos/${id}/processos`, 'GET');
    if (response.status === 200) {

      setProcessosRelacionados(response.data);
    } else {

    }
  }, [id]);

  // Função específica para usuários VIEWER e CLIENTE
  const verificarSeObservaEnvolvido = useCallback(async () => {
    if (!id || !isClienteOrViewer || !authUser?.id) return;

    try {
      const response = await makeRequest(`/users/${authUser.id}/observando-envolvidos`, 'GET');
      if (response.status === 200) {
        const observandoAtualmente = response.data.some(env => env.id === parseInt(id));

        setObservandoEnvolvido(observandoAtualmente);
      }
    } catch (error) {

    }
  }, [id, isClienteOrViewer, authUser?.id]);

  useEffect(() => {

    if (id) {

      getEnvolvido();
      getProcessosRelacionados();
      
      // Para usuários VIEWER e CLIENTE, verificar se já observa o envolvido
      if (isClienteOrViewer) {
        verificarSeObservaEnvolvido();
      }
    }
  }, [id]);
  
  // Executa verificação de observação quando dados do usuário mudam
  useEffect(() => {
    if (id && isClienteOrViewer && authUser?.id) {

      verificarSeObservaEnvolvido();
    }
  }, [isClienteOrViewer, authUser?.id]);
  
  // Executa a busca inicial e quando as dependências de busca mudam
  useEffect(() => {

    getProcessos();
  }, [getProcessos]);
  
  // Reset page quando sorting muda
  useEffect(() => {

    setPage(0);
  }, [sortBy, sortOrder]);
  
  // Função para buscar processo específico pelo número
  const handleBuscarProcessoPorNumero = async () => {
    if (!id || !numeroProcessoBusca.trim()) {

      return;
    }

    try {
      const response = await makeRequest(`/processos-revistas-rpi?num_processo=${encodeURIComponent(numeroProcessoBusca.trim())}`, 'GET');
      
      if (response.status === 200 && response.data.processos && response.data.processos.length > 0) {
        const processo = response.data.processos[0];

        setProcessoParaAdicionar(processo);
        setConfirmAddDialogOpen(true);
      } else {

        // Aqui você pode adicionar uma notificação para o usuário
        alert(`Nenhum processo encontrado com o número: ${numeroProcessoBusca}`);
      }
    } catch (error) {

      alert('Erro ao buscar o processo. Tente novamente.');
    }
  };
  const handleConfirmAddProcesso = async () => {
    if (!processoParaAdicionar) return;

    try {
      await makeRequest(`/processos-revistas-rpi/${processoParaAdicionar.id}/envolvidos`, 'POST', { envolvido_id: id });
      setConfirmAddDialogOpen(false);
      setProcessoParaAdicionar(null);
      setNumeroProcessoBusca("");

      // Aguarda um pouco antes de recarregar para garantir que o backend processou
      setTimeout(() => {
        getProcessos();
      }, 100);
      
      // Notificação de sucesso
      alert('Processo adicionado com sucesso!');
    } catch (error) {

      alert('Erro ao adicionar o processo. Tente novamente.');
    }
  };
  const handleCancelAddProcesso = () => {
    setConfirmAddDialogOpen(false);
    setProcessoParaAdicionar(null);
  };

  const handleObservarEnvolvido = async () => {
    if (!id || !authUser?.id) return;

    try {
      const response = await makeRequest('/users/observar-processos', 'POST', {
        userId: authUser.id,
        envolvidoId: parseInt(id)
      });
      
      if (response.status === 200) {
        setObservandoEnvolvido(true);
        setConfirmObservarDialogOpen(false);

        alert('Você agora está observando este envolvido!');
      } else {

        alert('Erro ao observar envolvido. Tente novamente.');
      }
    } catch (error) {

      alert('Erro ao observar envolvido. Tente novamente.');
    }
  };

  const handlePararObservarEnvolvido = async () => {
    if (!id || !authUser?.id) return;

    try {
      const response = await makeRequest(`/users/${authUser.id}/parar-observar-envolvido/${id}`, 'DELETE');
      
      if (response.status === 200) {
        setObservandoEnvolvido(false);

        alert('Você parou de observar este envolvido.');
      } else {

        alert('Erro ao parar observação. Tente novamente.');
      }
    } catch (error) {

      alert('Erro ao parar observação. Tente novamente.');
    }
  };

  return (
    <JumboContentLayout
      header={
        <HeaderPages
          title={`Envolvidos > ${id ? "Editar" : "Criar"} envolvido`}
          hasButton={true}
          isBackButton={true}
        />
      }
    >
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Confirmar associação de processos"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Você selecionou {selectedProcessos.length} processo(s) para associar a este envolvido. 
            Deseja realmente associar estes processos?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelProcessAssociation} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleConfirmProcessAssociation} color="primary" autoFocus>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
      {/* Modal de confirmação para adicionar processo específico */}
      <Dialog
        open={confirmAddDialogOpen}
        onClose={handleCancelAddProcesso}
        aria-labelledby="confirm-add-processo-title"
        aria-describedby="confirm-add-processo-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="confirm-add-processo-title">
          Confirmar adição de processo
        </DialogTitle>
        <DialogContent>
          {processoParaAdicionar && (
            <Box>
              <DialogContentText id="confirm-add-processo-description" mb={2}>
                Deseja adicionar o seguinte processo ao envolvido?
              </DialogContentText>
              <Box 
                p={2} 
                bgcolor="grey.50" 
                borderRadius={1}
                border="1px solid"
                borderColor="grey.300"
              >
                <Typography variant="h6" gutterBottom>
                  Processo Nº {processoParaAdicionar.numero}
                </Typography>
                {processoParaAdicionar.marca && (
                  <Typography variant="body2" color="textSecondary">
                    <strong>Marca:</strong> {processoParaAdicionar.marca.nome}
                  </Typography>
                )}
                {processoParaAdicionar.situacao && (
                  <Typography variant="body2" color="textSecondary">
                    <strong>Situação:</strong> {processoParaAdicionar.situacao}
                  </Typography>
                )}
                {processoParaAdicionar.procurador && (
                  <Typography variant="body2" color="textSecondary">
                    <strong>Procurador:</strong> {processoParaAdicionar.procurador}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCancelAddProcesso} color="inherit">
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmAddProcesso} 
            color="primary" 
            variant="contained"
            autoFocus
          >
            Adicionar Processo
          </Button>
        </DialogActions>
      </Dialog>
      {/* Confirmação para remover associação de processo */}
      <Dialog
        open={confirmDeleteDialogOpen}
        onClose={() => setConfirmDeleteDialogOpen(false)}
        aria-labelledby="confirm-delete-processo-title"
        aria-describedby="confirm-delete-processo-description"
      >
        <DialogTitle id="confirm-delete-processo-title">
          Confirmar remoção de associação
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-delete-processo-description">
            Tem certeza que deseja remover a associação do processo Nº {processoParaRemover?.numero} deste envolvido?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmDeleteDialogOpen(false); setProcessoParaRemover(null); }} color="primary">
            Cancelar
          </Button>
          <Button onClick={async () => {
            if (processoParaRemover) {

              await makeRequest(`/processos-revistas-rpi/${processoParaRemover.id}/envolvidos/${id}`, 'DELETE');
              setConfirmDeleteDialogOpen(false);
              setProcessoParaRemover(null);

              // Aguarda um pouco antes de recarregar para garantir que o backend processou
              setTimeout(() => {
                getProcessos();
              }, 100);
            }
          }} color="primary" autoFocus>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmação para usuários VIEWER e CLIENTE observarem envolvido */}
      {isClienteOrViewer && (
        <Dialog
          open={confirmObservarDialogOpen}
          onClose={() => setConfirmObservarDialogOpen(false)}
          aria-labelledby="confirm-observar-title"
          aria-describedby="confirm-observar-description"
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle id="confirm-observar-title">
            Observar Envolvido
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="confirm-observar-description" mb={2}>
              Deseja observar este envolvido? Você receberá notificações sobre atualizações relacionadas aos processos deste envolvido.
            </DialogContentText>
            <Box 
              p={2} 
              bgcolor="grey.50" 
              borderRadius={1}
              border="1px solid"
              borderColor="grey.300"
            >
              <Typography variant="h6" gutterBottom>
                {envolvido.nome_identificacao}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Tipo:</strong> {envolvido.tipo_envolvido}
              </Typography>
              {envolvido.cpf_cnpj && (
                <Typography variant="body2" color="textSecondary">
                  <strong>CPF/CNPJ:</strong> {envolvido.cpf_cnpj}
                </Typography>
              )}
              {envolvido.email && (
                <Typography variant="body2" color="textSecondary">
                  <strong>Email:</strong> {envolvido.email}
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setConfirmObservarDialogOpen(false)} color="inherit">
              Cancelar
            </Button>
            <Button 
              onClick={handleObservarEnvolvido} 
              color="primary" 
              variant="contained"
              autoFocus
            >
              Observar
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <Card width="100%" my={1}>
        <Formik
          initialValues={envolvido}
          enableReinitialize
          validateOnBlur={true}
          validationSchema={
            id ? updateSchemaValidation : createSchemaValidation
          }
          onSubmit={handleSubmit}
        >
          {({
            values,
            setFieldValue,
            errors,
            handleBlur,
            handleChange,
            setFieldTouched,
            touched,
          }) => (
            <Form width="100%">
              <Grid container width={"100%"} spacing={2} p={2}>
                <Grid item xs={6}>
                  <TextField
                    name="nome_identificacao"
                    type="text"
                    value={values.nome_identificacao}
                    onChange={handleChange}
                    handleBlur={(e) => {
                      handleBlur(e);
                      setFieldTouched("nome_identificacao", true);
                    }}
                    fullWidth={true}
                    label="Nome / Identificação"
                    disabled={isClienteOrViewer}
                  />
                  {touched.nome_identificacao && errors.nome_identificacao && (
                    <div style={{ color: "red" }}>
                      {errors.nome_identificacao}
                    </div>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Select
                    name="tipo_envolvido"
                    value={values.tipo_envolvido}
                    onChange={handleChange}
                    fullWidth
                    displayEmpty
                    disabled={isClienteOrViewer}
                  >
                    <MenuItem value="AGENTE">Agente</MenuItem>
                    <MenuItem value="CLIENTE">Cliente</MenuItem>
                    <MenuItem value="OUTRA_PARTE">Outra Parte</MenuItem>
                    <MenuItem value="OUTROS">Outros</MenuItem>
                    <MenuItem value="TERCEIRO">Terceiro</MenuItem>
                  </Select>
                  {touched.tipo_envolvido && errors.tipo_envolvido && (
                    <div style={{ color: "red" }}>{errors.tipo_envolvido}</div>
                  )}
                </Grid>

                <Grid item xs={6}>
                  <FormControl component="fieldset" fullWidth disabled={isClienteOrViewer}>
                    <FormLabel component="legend" sx={{ mb: 1 }}>
                      Tipo de pessoa
                    </FormLabel>
                    <RadioGroup
                      row
                      name="tipo_pessoa"
                      value={values.tipo_pessoa}
                      onChange={handleChange}
                    >
                      <FormControlLabel
                        value="fisica"
                        control={<Radio />}
                        label="Física"
                      />
                      <FormControlLabel
                        value="juridica"
                        control={<Radio />}
                        label="Jurídica"
                      />
                    </RadioGroup>
                    {touched.tipo_pessoa && errors.tipo_pessoa && (
                      <div style={{ color: "red" }}>{errors.tipo_pessoa}</div>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={6} my={1}>
                  <TextField
                    name="cpf_cnpj"
                    type="text"
                    value={values.cpf_cnpj}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    label="CPF / CNPJ"
                    disabled={isClienteOrViewer}
                  />
                  {touched.cpf_cnpj && errors.cpf_cnpj && (
                    <div style={{ color: "red" }}>{errors.cpf_cnpj}</div>
                  )}
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    name="cadastro_externo"
                    type="text"
                    value={values.cadastro_externo}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    label="Cadastro externo"
                    disabled={isClienteOrViewer}
                  />
                  {touched.cadastro_externo && errors.cadastro_externo && (
                    <div style={{ color: "red" }}>
                      {errors.cadastro_externo}
                    </div>
                  )}
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    name="inscricao_municipal"
                    type="text"
                    value={values.inscricao_municipal}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    label="Inscrição municipal"
                    disabled={isClienteOrViewer}
                  />
                  {touched.inscricao_municipal &&
                    errors.inscricao_municipal && (
                      <div style={{ color: "red" }}>
                        {errors.inscricao_municipal}
                      </div>
                    )}
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    name="inscricao_estadual"
                    type="text"
                    value={values.inscricao_estadual}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    label="Inscrição estadual"
                    disabled={isClienteOrViewer}
                  />
                  {touched.inscricao_estadual && errors.inscricao_estadual && (
                    <div style={{ color: "red" }}>
                      {errors.inscricao_estadual}
                    </div>
                  )}
                </Grid>

                <Grid item xs={12} mt={2}>
                  <Divider />
                  <Typography variant="h6" mt={2} mb={2}>
                    Contatos
                  </Typography>
                  {values.contatos.map((contato, idx) => (
                    <Box key={idx} display="flex" alignItems="center" mb={1} gap={2}>
                      <FormControl sx={{ minWidth: 150 }} size="small">
                        <InputLabel>Tipo</InputLabel>
                        <Select
                          name={`contatos[${idx}].tipo_contato`}
                          value={contato.tipo_contato}
                          label="Tipo"
                          onChange={handleChange}
                          disabled={isClienteOrViewer}
                        >
                          {contatoTiposEnum.map(tipo => (
                            <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        name={`contatos[${idx}].nome`}
                        value={contato.nome}
                        onChange={handleChange}
                        label="Nome"
                        size="small"
                        sx={{ minWidth: 180 }}
                        disabled={isClienteOrViewer}
                      />
                      <TextField
                        name={`contatos[${idx}].telefone`}
                        value={contato.telefone}
                        onChange={handleChange}
                        label="Telefone"
                        size="small"
                        sx={{ minWidth: 150 }}
                        disabled={isClienteOrViewer}
                      />
                      {/* campo valor removido, conforme DTO */}
                      {values.contatos.length > 1 && !isClienteOrViewer && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            const newArr = [...values.contatos];
                            newArr.splice(idx, 1);
                            setFieldValue("contatos", newArr);
                          }}
                        >Remover</Button>
                      )}
                      {idx === values.contatos.length - 1 && !isClienteOrViewer && (
                        <Button
                          size="small"
                          color="primary"
                          onClick={() => {
                            setFieldValue("contatos", [...values.contatos, { tipo_contato: "FINANCEIRO", nome: "", telefone: "" }]);
                          }}
                        >+ Adicionar contato</Button>
                      )}
                    </Box>
                  ))}
                  {touched.contatos && errors.contatos && (
                    <div style={{ color: "red" }}>{typeof errors.contatos === "string" ? errors.contatos : null}</div>
                  )}
                </Grid>

                {/* Campos de endereço */}
                <Grid item xs={4}>
                  <TextField
                    name="cep"
                    type="text"
                    value={values.cep}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    label="CEP"
                    disabled={isClienteOrViewer}
                  />
                  {touched.cep && errors.cep && (
                    <div style={{ color: "red" }}>{errors.cep}</div>
                  )}
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    name="logradouro"
                    type="text"
                    value={values.logradouro}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    label="Logradouro"
                    disabled={isClienteOrViewer}
                  />
                  {touched.logradouro && errors.logradouro && (
                    <div style={{ color: "red" }}>{errors.logradouro}</div>
                  )}
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    name="numero"
                    type="text"
                    value={values.numero}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    label="Número"
                    disabled={isClienteOrViewer}
                  />
                  {touched.numero && errors.numero && (
                    <div style={{ color: "red" }}>{errors.numero}</div>
                  )}
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    name="complemento"
                    type="text"
                    value={values.complemento}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    label="Complemento"
                    disabled={isClienteOrViewer}
                  />
                  {touched.complemento && errors.complemento && (
                    <div style={{ color: "red" }}>{errors.complemento}</div>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    name="bairro"
                    type="text"
                    value={values.bairro}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    label="Bairro"
                    disabled={isClienteOrViewer}
                  />
                  {touched.bairro && errors.bairro && (
                    <div style={{ color: "red" }}>{errors.bairro}</div>
                  )}
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    name="cidade"
                    type="text"
                    value={values.cidade}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    label="Cidade"
                    disabled={isClienteOrViewer}
                  />
                  {touched.cidade && errors.cidade && (
                    <div style={{ color: "red" }}>{errors.cidade}</div>
                  )}
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    name="uf"
                    type="text"
                    value={values.uf}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    label="UF"
                    disabled={isClienteOrViewer}
                  />
                  {touched.uf && errors.uf && (
                    <div style={{ color: "red" }}>{errors.uf}</div>
                  )}
                </Grid>
                  {touched.contatos && errors.contatos && (
                    <div style={{ color: "red" }}>{typeof errors.contatos === "string" ? errors.contatos : null}</div>
                  )}
                </Grid>

                <Grid item xs={12} mt={3}>
                  <Divider />
                  <Typography variant="h6" mt={2} mb={2}>
                    {isClienteOrViewer ? "Processos deste envolvido" : "Processos associados a este envolvido"}
                  </Typography>

                  {/* Para usuários VIEWER e CLIENTE, mostrar botão de observar/parar de observar */}
                  {isClienteOrViewer && id && (
                    <Box mb={3} p={2} bgcolor="blue.50" borderRadius={1} border="1px solid" borderColor="blue.200">
                      <Typography variant="subtitle1" mb={2} fontWeight={600} color="primary">
                        Observação de Envolvido
                      </Typography>
                      <Typography variant="body2" color="textSecondary" mb={2}>
                        {observandoEnvolvido 
                          ? "Você está observando este envolvido e receberá notificações sobre atualizações."
                          : "Observe este envolvido para receber notificações sobre atualizações nos processos relacionados."
                        }
                      </Typography>
                      <Box display="flex" gap={2}>
                        {observandoEnvolvido ? (
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={handlePararObservarEnvolvido}
                            size="small"
                          >
                            Parar de Observar
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => setConfirmObservarDialogOpen(true)}
                            size="small"
                          >
                            Observar Envolvido
                          </Button>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Barra de busca para adicionar processo específico - APENAS para ADMIN/OPERADOR */}
                  {id && (isAdmin || isOperador) && (
                    <Box mb={3} p={2} bgcolor="grey.50" borderRadius={1}>
                      <Typography variant="subtitle1" mb={2} fontWeight={600}>
                        Adicionar processo por número
                      </Typography>
                      <Box display="flex" gap={2} alignItems="flex-start">
                        <TextField
                          fullWidth
                          label="Número do processo"
                          placeholder="Digite o número exato do processo"
                          value={numeroProcessoBusca}
                          onChange={(e) => {

                            setNumeroProcessoBusca(e.target.value);
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && numeroProcessoBusca.trim()) {
                              handleBuscarProcessoPorNumero();
                            }
                          }}
                          variant="outlined"
                          size="small"
                        />
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleBuscarProcessoPorNumero}
                          disabled={!numeroProcessoBusca.trim()}
                          startIcon={<SearchIcon />}
                          sx={{ minWidth: '120px', height: '40px' }}
                        >
                          Buscar
                        </Button>
                      </Box>
                      <Typography variant="caption" color="textSecondary" mt={1} display="block">
                        Digite o número exato do processo e clique em "Buscar" para adicionar ao envolvido
                      </Typography>
                    </Box>
                  )}

                  {/* Lista de processos associados */}
                  <Box sx={{ mb: 3 }}>
                    <SelectableProcessList
                      processos={processos}
                      selectedProcessos={selectedProcessos}
                      onProcessoToggle={isClienteOrViewer ? undefined : handleProcessoToggle}
                      onRemoveProcesso={isClienteOrViewer ? undefined : async (processo) => {
                        setProcessoParaRemover(processo);
                        setConfirmDeleteDialogOpen(true);
                      }}
                      loading={carregandoMais}
                      readOnly={isClienteOrViewer}
                    />
                  </Box>
                  
                  {processos.length > 0 && (
                    <PaginationComponent
                      count={totalProcessos}
                      page={page}
                      perPage={perPage}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      setPage={setPage}
                      setPerPage={setPerPage}
                      setSortBy={setSortBy}
                      setSortOrder={setSortOrder}
                    />
                  )}
                </Grid>

                {/* Botão de submissão - APENAS para ADMIN/OPERADOR */}
                {(isAdmin || isOperador) && (
                  <Box
                    width="100%"
                    my={1}
                    display={"flex"}
                    justifyContent={"flex-end"}
                    p={2}
                  >
                    <Button
                      color="info"
                      type="submit"
                      variant="contained"
                      onClick={e => {

                        // Não previne o default, deixa o Formik processar normalmente
                      }}
                    >
                      {id ? "Editar envolvido" : "Criar envolvido"}
                    </Button>
                  </Box>
                )}
            </Form>
          )}
        </Formik>
      </Card>
    </JumboContentLayout>
  );
};

export default React.memo(EnvolvidosForm);
